/**
 * Web Push subscription hook.
 *
 * Behaviour:
 *   - Mounted globally inside <App />.
 *   - Does NOTHING until the user is signed in AND has explicitly opted in
 *     by clicking "Enable on this device" in Settings → Notifications.
 *     We never auto-prompt for the Notification permission on page load —
 *     that wrecks UX and gets us blocked by aggressive browsers.
 *   - When opted-in + permission granted, registers `/push-sw.js`,
 *     subscribes via the browser PushManager using our VAPID public key,
 *     and POSTs the subscription to `register-device-token`.
 *   - Suppressed inside the Lovable preview iframe to avoid polluting the
 *     editor with stale service workers.
 *
 * Public API:
 *   usePushSubscription()          // mount once
 *   await enablePushOnThisDevice() // call from a click handler
 *   await disablePushOnThisDevice()
 *   getPushState()                 // 'unsupported' | 'denied' | 'default' | 'granted-no-sub' | 'subscribed'
 */
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isNativeShell } from "@/lib/runtime";

const PUBLIC_VAPID_KEY_LS = "aicavite.vapidPublicKey";

function isInPreviewIframe(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.self !== window.top) return true;
  } catch {
    return true;
  }
  const h = window.location.hostname;
  return h.includes("id-preview--") || h.includes("lovableproject.com");
}

function urlBase64ToBuffer(base64: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const buf = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return buf;
}

async function fetchVapidPublicKey(): Promise<string | null> {
  // Cache in localStorage to avoid a round-trip every session.
  const cached = localStorage.getItem(PUBLIC_VAPID_KEY_LS);
  if (cached) return cached;
  // The dispatcher exposes the configured key list via the test endpoint.
  // For the public key we use a small dedicated read: settings table is
  // overkill, so we just include it in the build via VITE_ env var.
  const fromEnv = (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined) ?? "";
  if (fromEnv) {
    localStorage.setItem(PUBLIC_VAPID_KEY_LS, fromEnv);
    return fromEnv;
  }
  return null;
}

export type PushState =
  | "unsupported"
  | "denied"
  | "default"
  | "granted-no-sub"
  | "subscribed";

export async function getPushState(): Promise<PushState> {
  if (typeof window === "undefined") return "unsupported";
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    return "unsupported";
  }
  if (Notification.permission === "denied") return "denied";
  if (Notification.permission === "default") return "default";
  const reg = await navigator.serviceWorker.getRegistration("/push-sw.js");
  if (!reg) return "granted-no-sub";
  const sub = await reg.pushManager.getSubscription();
  return sub ? "subscribed" : "granted-no-sub";
}

export async function enablePushOnThisDevice(): Promise<{ ok: boolean; error?: string }> {
  if (isInPreviewIframe()) return { ok: false, error: "Disabled inside the editor preview." };
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { ok: false, error: "Push is not supported on this browser." };
  }

  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, error: `Permission: ${perm}` };

  const vapid = await fetchVapidPublicKey();
  if (!vapid) {
    return {
      ok: false,
      error:
        "VAPID public key is not configured yet. Add VITE_VAPID_PUBLIC_KEY in build settings (and the matching VAPID_PRIVATE_KEY backend secret).",
    };
  }

  const reg = await navigator.serviceWorker.register("/push-sw.js", { scope: "/" });
  await navigator.serviceWorker.ready;

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToBuffer(vapid),
    });
  }

  const json = sub.toJSON();
  const platform = (window as any).aicavite?.isElectron ? "electron" : "web";

  const { error } = await supabase.functions.invoke("register-device-token", {
    body: {
      platform,
      token: sub.endpoint, // unique per browser+endpoint, fits our (user_id, token) unique key
      endpoint: sub.endpoint,
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
    },
  });

  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

export async function disablePushOnThisDevice(): Promise<{ ok: boolean; error?: string }> {
  if (!("serviceWorker" in navigator)) return { ok: true };
  const reg = await navigator.serviceWorker.getRegistration("/push-sw.js");
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try {
      await supabase.functions.invoke("unregister-device-token", {
        body: { token: sub.endpoint },
      });
    } catch {
      /* ignore — best effort */
    }
    await sub.unsubscribe();
  }
  await reg.unregister();
  return { ok: true };
}

/**
 * Mount this once at the top of the app. It only refreshes `last_seen_at`
 * if the user is already subscribed — it never prompts.
 */
export function usePushSubscription() {
  useEffect(() => {
    if (isInPreviewIframe()) return;
    if (isNativeShell() && (window as any).Capacitor?.isNativePlatform?.()) {
      // Capacitor mobile uses APNs/FCM via native-push.ts — skip Web Push.
      return;
    }
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

    let cancelled = false;

    (async () => {
      try {
        const { data: sess } = await supabase.auth.getSession();
        if (!sess.session?.user || cancelled) return;

        const reg = await navigator.serviceWorker.getRegistration("/push-sw.js");
        if (!reg) return;
        const sub = await reg.pushManager.getSubscription();
        if (!sub) return;

        const json = sub.toJSON();
        const platform = (window as any).aicavite?.isElectron ? "electron" : "web";
        await supabase.functions.invoke("register-device-token", {
          body: {
            platform,
            token: sub.endpoint,
            endpoint: sub.endpoint,
            p256dh: json.keys?.p256dh,
            auth: json.keys?.auth,
          },
        });
      } catch {
        // Silent — push refresh is best-effort.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);
}
